/**
 * 사이드바 네비게이션 **레지스트리** (설계 §2-2(3)).
 *
 * 정보구조는 Figma `관리자 대시보드`(13989-46325)의 `aside > nav` 실측값이며,
 * 첨부 `구장_관리자_웹_유저플로우.html` 의 사이드바와 1:1로 일치한다.
 *
 * 🚨 **후속 화면이 붙는 방법**: `pages/XxxPage.tsx` 를 만들고 `routes/router.tsx` 의
 * `venueChildren` 에 라우트 1개를 추가한 뒤, 여기서 해당 항목의 `status` 를
 * `'planned'` → `'ready'` 로 바꾼다. 그게 전부다 — 셸·인증·API 계층은 건드리지 않는다.
 *
 * `planned` 항목은 **링크가 아니라 비활성 텍스트 + `준비 중` 배지**로 렌더한다.
 * 죽은 링크(404)를 만들지 않으면서 확정된 IA 는 그대로 보여주기 위함이다(설계 D4).
 */
export type NavStatus = 'ready' | 'planned'

export interface NavItem {
  label: string
  /** `/venues/:venueId/` 뒤에 붙는 상대 경로. 빈 문자열이면 구장 홈(index). */
  path: string
  status: NavStatus
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: '구장 현황',
    items: [
      { label: '구장 홈', path: '', status: 'ready' },
      { label: '대시보드', path: 'dashboard', status: 'ready' },
    ],
  },
  {
    label: '구장·코트 설정',
    items: [
      // 🚨 #595 로 3항목 모두 `ready` 전환됐다. `courts`·`closures` 는 코트 CRUD·휴무 도메인
      // 자체가 아직 없어 플레이스홀더 화면으로 연결한다(요구사항 "라우트만 연결" — 설계 §2-0 D2).
      { label: '구장 기본정보', path: 'venue', status: 'ready' },
      { label: '코트 관리', path: 'courts', status: 'ready' },
      { label: '휴무일·차단시간', path: 'closures', status: 'ready' },
    ],
  },
  {
    label: '예약 관리',
    items: [
      // 🚨 #667 — 예약 조회 계약(백엔드 #666)이 확정되어 플레이스홀더를 실제 화면으로 교체했다.
      { label: '예약 캘린더', path: 'bookings', status: 'ready' },
      { label: '예약 목록', path: 'bookings/list', status: 'ready' },
      // 🚨 #758 — 예약 기능 사용 설정 계약(백엔드 #757)이 확정되어 화면을 연다.
      { label: '예약 기능 설정', path: 'reservation-setting', status: 'ready' },
    ],
  },
  {
    label: '공지·홍보',
    // 🚨 #729 — 공지·홍보 발송 계약(백엔드 #714·#715·#728)이 확정되어 플레이스홀더를 실제 화면으로 교체했다.
    items: [{ label: '공지·홍보', path: 'notices', status: 'ready' }],
  },
  {
    label: '매출·정산',
    // 🚨 #704 — 매출·정산·환불 결과 조회 계약(백엔드 #703)이 확정되어 플레이스홀더를 실제 화면으로
    // 교체했다. 하위 4화면(매출 현황·정산 확정예정·환불 결과·차감 내역)은 `bookings/:reservationId`와
    // 같은 관례로 `navItems` 미등록 — 홈(`sales`)의 하위 메뉴 링크로만 진입한다.
    items: [{ label: '매출·정산', path: 'sales', status: 'ready' }],
  },
]

/** 라우터에 실제로 존재해야 하는 경로들 — 레지스트리·라우터 동기화 테스트가 쓴다. */
export function readyNavPaths(groups: NavGroup[] = NAV_GROUPS): string[] {
  return groups.flatMap((group) =>
    group.items.filter((item) => item.status === 'ready').map((item) => item.path)
  )
}

/** 구장 하위 절대 경로를 만든다. */
export function navItemHref(venueId: string, item: NavItem): string {
  return item.path === '' ? `/venues/${venueId}` : `/venues/${venueId}/${item.path}`
}
